import { IRegEdSubmissionMetadata } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { accountAllowsComplianceBypass } from '../../../../../models/UiUtils';
import { isSplitByPlatform } from '../../../../../models/utils/platformCustomizations';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import {
	getMediaEntry,
	removeMediaEntry,
} from '../../../../components/socialMedia/SocialMediaImageSearchAndDisplay/mediaEntries';
import { MultiContainerHeader } from '../../../../components/MultiContainerHeader';
import { IEditSocialMediaPostContext } from '../context';
import { MobileActionButtons } from './components/MobileActionButtons';
import { MobileContentEditor } from './components/MobileContentEditor';
import { MobileImageCarousel } from './components/MobileImageCarousel';
import { MobileWhereToShare } from './components/MobileWhereToShare';
import { mobileStyles } from './styles';

interface MobileEditSocialMediaPostProps {
	context: IEditSocialMediaPostContext;
	socialMediaConnections: Api.ISocialMediaConnection[];
	onSelectedConnectionsChanged: (selected: Api.IPostTarget[]) => void;
	onRequestClose: () => void;
	onScheduleForLater: () => void;
	onPostNow: () => void;
	onFreeImages?: () => void;
	onUploadMedia?: () => void;
	onShowCancelUpdateConfirmation?: (e: React.MouseEvent<HTMLElement>) => void;
	isSchedulingInProgress: boolean;
	postToEdit?: Api.SocialMediaPostViewModel;
	scheduledPost?: Api.SocialMediaPostViewModel;
	source?: Api.SocialMediaPostReportViewModel | Api.SocialMediaPostViewModel;
	errorMessages?: any;
	isLoaded: boolean;
	usesRegEd?: boolean;
	/** Officer files from the review portal, so the advisor fills no questionnaire here. */
	officerFilesRegEd?: boolean;
	officerReviewerMissing?: boolean;
	regEdProbeLoading?: boolean;
	onScheduleRegEdCompliance?: (e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionMetadata) => void;
	onScheduleWithoutCompliance?: (e: React.MouseEvent<HTMLElement>) => void;
	regEdMetadata?: IRegEdSubmissionMetadata | null;
	impersonationCtx?: Api.IImpersonationContext;
}

const MobileEditSocialMediaPost: React.FC<MobileEditSocialMediaPostProps> = observer(
	({
		context,
		socialMediaConnections,
		onSelectedConnectionsChanged,
		onScheduleForLater,
		onPostNow,
		onFreeImages,
		onUploadMedia,
		isSchedulingInProgress,
		source,
		errorMessages,
		isLoaded,
		usesRegEd,
		officerFilesRegEd,
		officerReviewerMissing,
		regEdProbeLoading,
		onScheduleRegEdCompliance,
		onScheduleWithoutCompliance,
		regEdMetadata,
		impersonationCtx,
	}) => {
		const userSession = useUserSession();
		const readOnly = source ? !source.canEdit : false;
		const isSplit = isSplitByPlatform(context.customizeByPlatform, context.postTargets);

		const handleContentChange = (content: string) => {
			context.setContent(content);
		};

		const handleImageRemove = (index: number) => {
			const entry = getMediaEntry(context.postImages ?? [], index);
			context.setActiveImages(images => removeMediaEntry(images, entry), undefined);
		};

		const canPostNow = Boolean(
			context.postTargets?.length &&
				context.postContent?.length &&
				!isSchedulingInProgress &&
				userSession?.user?.id === context.selectedCreator?.id
		);

		const canSchedule = Boolean(context.postTargets?.length && context.postContent?.length && !isSchedulingInProgress);

		// Read straight off the session: the impersonated case is excluded on the same line, so there
		// is no context to resolve and no reason to build a tracked observable per render.
		const canBypassCompliance = !impersonationCtx && accountAllowsComplianceBypass(userSession, undefined, usesRegEd);
		// Withheld once the prompt asks the question — Post Now would be a third answer to it.
		const showPostNow =
			userSession?.user?.id === context.selectedCreator?.id &&
			!readOnly &&
			!regEdProbeLoading &&
			(!usesRegEd || (canBypassCompliance && !onScheduleRegEdCompliance));

		if (!isLoaded) {
			return (
				<div className={css(mobileStyles.container)}>
					<div className={css(mobileStyles.loadingContainer)}>
						<LoadingSpinner type='large' />
						<div className={css(mobileStyles.loadingText)}>Loading post...</div>
					</div>
				</div>
			);
		}
		return (
			<div className={css(mobileStyles.container)}>
				<MultiContainerHeader fullscreenHeader={'Create Post'} />
				<div className={css(mobileStyles.content)}>
					<div className={css(mobileStyles.scrollableContent)}>
						{errorMessages?.messages?.length > 0 && (
							<div className={css(mobileStyles.errorMessage)}>
								{errorMessages.messages.map((msg: string, index: number) => (
									<div key={index}>{msg}</div>
								))}
							</div>
						)}

						{context.selectedCreator && !socialMediaConnections?.length && (
							<div className={css(mobileStyles.warningMessage)}>
								Selected user is currently not linked to any social media accounts.
							</div>
						)}

						{context.selectedCreator && (
							<div className={css(mobileStyles.section)}>
								<MobileWhereToShare
									connections={socialMediaConnections || []}
									selectedTargets={context.postTargets || []}
									onSelectionChanged={onSelectedConnectionsChanged}
									readOnly={readOnly}
								/>
							</div>
						)}

						{/* Split, the base holds every tab's uploads and none of the crops, which is no version's media. */}
						{!isSplit && context.postImages && context.postImages.length > 0 && (
							<div className={css(mobileStyles.section)}>
								<MobileImageCarousel
									images={context.postImages}
									onImageRemove={readOnly ? undefined : handleImageRemove}
								/>
							</div>
						)}

						<div className={css(mobileStyles.section)}>
							{isSplit ? (
								<div className={css(mobileStyles.warningMessage)}>
									This post has its own version for each platform. Those versions are what publish, so open it on a
									computer to edit them.
								</div>
							) : null}
							<MobileContentEditor
								content={context.postContent || ''}
								onContentChange={handleContentChange}
								readOnly={readOnly || isSplit}
							/>
						</div>
					</div>

					{!readOnly && (
						<MobileActionButtons
							onScheduleForLater={onScheduleForLater}
							onPostNow={onPostNow}
							// A split post publishes each version's own media, which only the desktop composer edits.
							onUploadMedia={isSplit ? undefined : onUploadMedia}
							onFreeImages={isSplit ? undefined : onFreeImages}
							canPostNow={canPostNow}
							canSchedule={canSchedule}
							isSchedulingInProgress={isSchedulingInProgress}
							showPostNow={showPostNow}
							imageCount={context.postImages?.length || 0}
							usesRegEd={usesRegEd}
							officerFilesRegEd={officerFilesRegEd}
							officerReviewerMissing={officerReviewerMissing}
							onScheduleRegEdCompliance={onScheduleRegEdCompliance}
							onScheduleWithoutCompliance={canBypassCompliance ? onScheduleWithoutCompliance : undefined}
							regEdMetadata={regEdMetadata}
							socialProviders={context.postTargets?.map(target => target.provider)}
							impersonationCtx={impersonationCtx}
						/>
					)}
				</div>
			</div>
		);
	}
);

export { MobileEditSocialMediaPost };
